import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { getSportConfig, toConfiguredSportType } from '@/utils/sportConfiguration';
import { getSportMeta } from '@/config/sports/registry';

const SPORTS = [
  { slug: 'soccer', emoji: '⚽', label: 'Soccer' },
  { slug: 'basketball', emoji: '🏀', label: 'Basketball' },
  { slug: 'baseball', emoji: '⚾', label: 'Baseball' },
  { slug: 'softball', emoji: '🥎', label: 'Softball' },
  { slug: 'flag-football', emoji: '🏈', label: 'Flag Football' },
] as const;

const HIGHLIGHTS: Record<string, string[]> = {
  soccer: ['Goals', 'Assists', 'Save %', 'Cards', 'Shot Accuracy', 'Pass Accuracy'],
  basketball: ['PPG', 'RPG', 'APG', 'FG%', 'eFG%', 'TS%'],
  baseball: ['AVG', 'OBP', 'SLG', 'OPS', 'ERA', 'WHIP'],
  softball: ['AVG', 'OBP', 'SLG', 'OPS', 'ERA', 'WHIP'],
  'flag-football': ['Passer Rating', 'Pass Yards', 'Receiving Yards', 'Flag Pulls', 'Stops', 'CMP%'],
};

const SportSelectorWidget: React.FC = () => {
  const [active, setActive] = useState('soccer');

  return (
    <Tabs value={active} onValueChange={setActive}>
      <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1 rounded-xl mb-6">
        {SPORTS.map(s => (
          <TabsTrigger
            key={s.slug}
            value={s.slug}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium"
          >
            <span>{s.emoji}</span>
            <span>{s.label}</span>
          </TabsTrigger>
        ))}
      </TabsList>

      {SPORTS.map(s => {
        const sportType = toConfiguredSportType(s.slug);
        const meta = getSportMeta(s.slug);
        const config = sportType ? getSportConfig(sportType) : null;
        const highlights = HIGHLIGHTS[s.slug] ?? [];
        const periods = config?.scoringPeriods ?? [];
        const periodName = periods[0]?.includes('Half')
          ? 'halves'
          : periods[0]?.includes('Q') || periods[0] === 'Q1'
          ? 'quarters'
          : 'innings';
        const tieRule = meta?.allowsTies ? 'Ties allowed' : 'No ties';
        const positionCount = config?.positions.length ?? 0;

        return (
          <TabsContent key={s.slug} value={s.slug}>
            <div className="bg-background rounded-xl border p-6 text-left">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
                <div>
                  <h3 className="text-lg font-bold mb-1">{s.label} leagues</h3>
                  <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                    <span>{periods.length} {periodName} per game</span>
                    <span className="text-border">|</span>
                    <span>{tieRule}</span>
                    <span className="text-border">|</span>
                    <span>{positionCount} positions tracked</span>
                  </div>
                </div>
                <Link
                  to={`/sports/${s.slug}`}
                  className="flex items-center gap-1 text-sm font-medium text-primary hover:underline shrink-0"
                >
                  See all {s.label} features <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Key stats tracked
                </p>
                <div className="flex flex-wrap gap-2">
                  {highlights.map(h => (
                    <span
                      key={h}
                      className="px-3 py-1 bg-primary/8 border border-primary/20 text-primary rounded-full text-sm font-medium"
                    >
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        );
      })}
    </Tabs>
  );
};

export default SportSelectorWidget;
